import { TeamResponseDto } from "@/lib/api/interfaces/teams.interface";
import { useFormConflictError } from "@/lib/hooks/use-form-conflict-error";
import {
  useTeamsApplyLogoEdit,
  useTeamsCreate,
  useTeamsPreviewBackgroundRemoval,
  useTeamsUploadLogo,
} from "@/lib/api/requests/teams.requests";
import { toast } from "@/lib/toast";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui-components/ui/form";
import { Input } from "@/ui-components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { UsersRound } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import z from "zod";
import { CreateBaseDialog } from "@/util-components/dialog/base-dialogs";
import { ManagedImageField, persistManagedImageDraft } from "@/util-components/media";

const createTeamSchema = z.object({
  title: z.string().min(1, { message: "Titel ist erforderlich" }),
  displayName: z.string().optional(),
  logo: z.instanceof(File).optional(),
});

type CreateTeamFormValues = z.infer<typeof createTeamSchema>;

const CreateTeamDialog = ({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: (refetchData: boolean) => void;
  onCreated?: (team: TeamResponseDto) => void;
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [canSave, setCanSave] = useState(false);

  return (
    <CreateBaseDialog
      open={open}
      onClose={onClose}
      icon={UsersRound}
      title="Mannschaft hinzufügen"
      description="Erstelle eine neue Mannschaft für deinen Verein."
      isSaving={isSaving}
      canSave={canSave}
      formId="create-team-form"
      maxWidth="lg"
    >
      <CreateTeamForm
        onFormSubmit={(team) => {
          onClose(true);
          if (team) {
            onCreated?.(team);
          }
        }}
        setIsSaving={setIsSaving}
        setCanSave={setCanSave}
      />
    </CreateBaseDialog>
  );
};

const CreateTeamForm = ({
  onFormSubmit,
  setIsSaving,
  setCanSave,
}: {
  onFormSubmit: (team?: TeamResponseDto) => void;
  setIsSaving: (val: boolean) => void;
  setCanSave: (val: boolean) => void;
}) => {
  const { mutateAsync: createTeam, isPending: isCreating } = useTeamsCreate(undefined, {
    skipGlobalErrorHandler: true,
    throwOnError: true,
  });
  const { mutateAsync: uploadLogo, isPending: isUploading } = useTeamsUploadLogo();
  const { mutateAsync: applyLogoEdit, isPending: isApplyingLogoEdit } = useTeamsApplyLogoEdit();
  const { mutateAsync: previewBackgroundRemoval } = useTeamsPreviewBackgroundRemoval();

  const form = useForm<CreateTeamFormValues>({
    resolver: zodResolver(createTeamSchema),
    mode: "onChange",
    defaultValues: {
      title: "",
      displayName: "",
      logo: undefined,
    },
  });

  const { handleConflictError } = useFormConflictError(form, { default: "title" });
  const logoFile = form.watch("logo");
  const isPending = isCreating || isUploading || isApplyingLogoEdit;

  useEffect(() => {
    setIsSaving(isPending);
  }, [isPending, setIsSaving]);

  useEffect(() => {
    setCanSave(form.formState.isValid);
  }, [form.formState.isValid, setCanSave]);

  const onSubmit = async (data: CreateTeamFormValues) => {
    try {
      const createdTeam = await createTeam({
        title: data.title,
        displayName: data.displayName || undefined,
      });

      await persistManagedImageDraft({
        entityId: createdTeam._id,
        file: data.logo,
        uploadImage: uploadLogo,
        applyImageEdit: applyLogoEdit,
      });

      toast.success(data.logo ? "Mannschaft und Wappen erfolgreich erstellt" : "Mannschaft wurde erfolgreich erstellt");
      onFormSubmit(createdTeam);
    } catch (error) {
      handleConflictError(error as Error, "Mannschaft konnte nicht erstellt werden");
    }
  };

  return (
    <Form {...form}>
      <form id="create-team-form" onSubmit={form.handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex-1 space-y-6 overflow-y-auto p-6">
          <FormField
            control={form.control}
            name="logo"
            render={() => (
              <FormItem>
                <FormControl>
                  <ManagedImageField
                    file={logoFile}
                    onFileChange={(nextFile) => form.setValue("logo", nextFile ?? undefined, { shouldDirty: true, shouldValidate: true })}
                    disabled={isPending}
                    busyAction={isPending ? "saving" : null}
                    emptyTitle="Wappen auswählen"
                    previewBackgroundRemoval={async (file) => previewBackgroundRemoval({ file })}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormLabel className="text-sm font-medium text-gray-700">
                    Mannschaftsname <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="z.B. C Jugend, Herren 3..."
                      {...field}
                      className="h-11 border-gray-200 transition-all focus:border-blue-500 focus:ring-blue-500/20"
                      disabled={isPending}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="displayName"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormLabel className="text-sm font-medium text-gray-700">Anzeigename</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="FC Musterhausen III"
                      {...field}
                      className="h-11 border-gray-200 transition-all focus:border-blue-500 focus:ring-blue-500/20"
                      disabled={isPending}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      </form>
    </Form>
  );
};

export default CreateTeamDialog;
